import { useState, useEffect } from 'react';
import { EnvelopeIcon, PencilIcon, XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useSettings } from '../../../../contexts/SettingsContext';
import EmailProviderFields from '../../../../components/settings/EmailProviderFields';
import { EMAIL_PROVIDERS } from '../../../../components/settings/EmailSettingsForm';
import type { EmailProviderConfig } from '../../../../components/settings/EmailSettingsForm';

const DEFAULT_CONFIG: EmailProviderConfig = {
  provider: 'smtp',
  from_email: '',
  from_name: '',
  smtp: { host: '', port: 587, username: '', password: '', secure: false },
};

export default function MobileSettingsEmail() {
  const { state, updateSystemSetting, addSystemSetting } = useSettings();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<EmailProviderConfig>(DEFAULT_CONFIG);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const setting = state.systemSettings.find(s => s.key === 'email_provider_config');
    if (setting?.value) {
      const raw = typeof setting.value === 'string' ? JSON.parse(setting.value) : setting.value;
      setConfig({ ...DEFAULT_CONFIG, ...raw });
    }
  }, [state.systemSettings]);

  const startEdit = () => {
    const setting = state.systemSettings.find(s => s.key === 'email_provider_config');
    if (setting?.value) {
      const raw = typeof setting.value === 'string' ? JSON.parse(setting.value) : setting.value;
      setConfig({ ...DEFAULT_CONFIG, ...raw });
    }
    setErrors({});
    setIsEditing(true);
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!config.from_email) errs.from_email = 'From email is required';
    if (!config.from_name) errs.from_name = 'From name is required';
    if (config.provider === 'sendgrid' && !config.sendgrid?.api_key) errs.api_key = 'API key required';
    if (config.provider === 'mailgun') {
      if (!config.mailgun?.api_key) errs.api_key = 'API key required';
      if (!config.mailgun?.domain) errs.domain = 'Domain required';
    }
    if (config.provider === 'postmark' && !config.postmark?.server_token) errs.server_token = 'Server token required';
    if (config.provider === 'resend' && !config.resend?.api_key) errs.api_key = 'API key required';
    if (config.provider === 'smtp') {
      if (!config.smtp?.host) errs.host = 'Host required';
      if (!config.smtp?.username) errs.username = 'Username required';
      if (!config.smtp?.password) errs.password = 'Password required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const existing = state.systemSettings.find(s => s.key === 'email_provider_config');
      if (existing) {
        await updateSystemSetting({ ...existing, value: config });
      } else {
        await addSystemSetting({ key: 'email_provider_config', value: config, category: 'email', description: 'Email provider configuration', is_public: false });
      }
      toast.success('Email settings saved');
      setIsEditing(false);
    } catch {
      toast.error('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const currentProvider = EMAIL_PROVIDERS.find(p => p.id === config.provider);

  if (isEditing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-golf-gray text-base">Edit Email Settings</h3>
          <button onClick={() => setIsEditing(false)} className="p-1 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">Provider</label>
          <div className="grid grid-cols-2 gap-2">
            {EMAIL_PROVIDERS.map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => setConfig(c => ({ ...c, provider: p.id as EmailProviderConfig['provider'] }))}
                className={`py-2.5 px-3 rounded-xl text-sm font-medium border transition-colors ${
                  config.provider === p.id
                    ? 'bg-golf-orange text-white border-golf-orange'
                    : 'bg-white text-gray-600 border-gray-200'
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">From Email *</label>
          <input
            type="email"
            value={config.from_email}
            onChange={e => setConfig(c => ({ ...c, from_email: e.target.value }))}
            className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange"
          />
          {errors.from_email && <p className="text-xs text-red-500 mt-1">{errors.from_email}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">From Name *</label>
          <input
            type="text"
            value={config.from_name}
            onChange={e => setConfig(c => ({ ...c, from_name: e.target.value }))}
            className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange"
          />
          {errors.from_name && <p className="text-xs text-red-500 mt-1">{errors.from_name}</p>}
        </div>

        <EmailProviderFields config={config} setConfig={setConfig} errors={errors} />

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-golf-orange text-white text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  const hasConfig = !!state.systemSettings.find(s => s.key === 'email_provider_config')?.value;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-rose-50 flex items-center justify-center">
              <EnvelopeIcon className="w-5 h-5 text-rose-500" />
            </div>
            <div>
              <p className="text-sm font-semibold text-golf-gray">Email Provider</p>
              <p className="text-xs text-gray-400">{hasConfig ? currentProvider?.name ?? config.provider : 'Not configured'}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {hasConfig
              ? <CheckCircleIcon className="w-4 h-4 text-green-500" />
              : <ExclamationTriangleIcon className="w-4 h-4 text-amber-400" />}
          </div>
        </div>

        {hasConfig && (
          <div className="space-y-1.5 border-t border-gray-50 pt-3">
            <div className="flex justify-between text-xs">
              <span className="text-gray-400">From</span>
              <span className="text-gray-700 font-medium">{config.from_name}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-400">Email</span>
              <span className="text-gray-700 font-medium truncate max-w-[160px]">{config.from_email}</span>
            </div>
          </div>
        )}

        <button
          onClick={startEdit}
          className="w-full mt-3 flex items-center justify-center gap-2 py-2 bg-gray-50 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <PencilIcon className="w-4 h-4" />
          {hasConfig ? 'Edit Settings' : 'Configure'}
        </button>
      </div>

      {!hasConfig && (
        <div className="bg-amber-50 rounded-2xl border border-amber-100 p-4">
          <p className="text-xs text-amber-700">Email is not configured. Set up a provider to enable automated emails for registration confirmations and notifications.</p>
        </div>
      )}
    </div>
  );
}
